import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { apagarLateralidade, gravarLateralidade } from 'utils/lateralidade';
import { canApproveParticipants } from 'pages/Dashboard/home-reads';
import { FOCO_SOBRE_AZUL } from 'utils/foco';

import { useServerTime } from 'hooks/useServerTime';
import { etapasDoCiclo, rotuloFaseAtual, textoDoPrazo } from './calendario-do-ciclo';
import CabecalhoDaMinhaEquipe from './components/CabecalhoDaMinhaEquipe';
import CalendarioDoCiclo from './components/CalendarioDoCiclo';
import ListaDaEquipe, { EstadoDaExportacao } from './components/ListaDaEquipe';
import ModalAdicionarFuncionario from './components/ModalAdicionarFuncionario';
import ModalAlterarGestor from './components/ModalAlterarGestor';
import ModalEquipeLateral, { GestorLateral } from './components/ModalEquipeLateral';
import PainelDoLiderado from './components/PainelDoLiderado';
import PanoramaDaEquipe from './components/PanoramaDaEquipe';
import { exportarEquipe } from './exportar-equipe';
import {
  faseDeReferencia,
  LinhaDaEquipe,
  linhaDaEquipe,
  membroDoResumo,
  totaisDoPanorama,
} from './minha-equipe-calculos';
import { useEquipe } from './useEquipe';
import { usePendenciasDoLider } from './usePendenciasDoLider';

// Classes globais de paginação (`.pagination`, `.item`, `.active`) que outras páginas ainda usam sem importar.
import './styles.scss';

/** "Arquivo gerado" fica visível por 2,6 s (EQP-51). */
export const DURACAO_DO_ARQUIVO_GERADO_MS = 2600;

const AVISO_DE_SOLICITACAO = 'Solicitação registrada. A equipe é atualizada na próxima etapa de apreciação.';

type Modal = 'adicionar' | 'alterar' | 'lateral' | null;

/**
 * A equipe lateral fica no histórico (MEQ2-05): entrar empilha uma entrada sobre a própria equipe, e o voltar
 * do navegador, a seta do cabeçalho e "Voltar para minha equipe" voltam a ela, e não à página inicial.
 */
interface EstadoDaPagina {
  equipeLateral?: GestorLateral;
  /**
   * A entrada foi empilhada pela página, logo a anterior é a própria equipe. O `history.state` sobrevive ao reload e
   * à aba restaurada; a marca só falta quando se entra com um estado que a página não empilhou.
   */
  sobreAPropriaEquipe?: boolean;
}

/** MEQ2-04: a rolagem até a lista só é suave para quem não pediu menos movimento. */
function rolagemDaLista(): ScrollBehavior {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/** Região viva sempre montada: o leitor de tela anuncia o aviso quando o conteúdo entra (EQP-40, EQP-41). */
function AvisoDeSolicitacao({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  return (
    <div role="status" className="pointer-events-none fixed inset-x-0 bottom-8 z-[64] flex justify-center px-4">
      {aberto && (
        // SPEC_DEVIATION: fundo `brand-blue-dark` (o "Pronto" amarelo de 13px sobre `brand-blue` dá 4,44:1, EQP-33).
        <div className="pointer-events-auto flex items-center gap-3 rounded-jj-lg bg-brand-blue-dark px-5 py-3.5 text-brand-white shadow-[0_12px_30px_rgba(0,45,75,0.28)]">
          <span className="font-display text-[13px] font-bold text-brand-yellow">Pronto</span>
          <span className="text-xs">{AVISO_DE_SOLICITACAO}</span>
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onFechar}
            className={`flex cursor-pointer border-0 bg-transparent p-0 text-brand-white ${FOCO_SOBRE_AZUL}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

/** Painel do líder: panorama da equipe no ciclo, lista com números e status, painel do liderado e calendário. */
export default function MinhaEquipe() {
  const { user, userInfo, cycles, currentCycle } = useAuth();
  const gestor = !!userInfo?.is_manager;
  const rede = !!user?.extra?.rede;

  const equipe = useEquipe(gestor ? user?.extra?.id : undefined);
  const navigate = useNavigate();
  const { pathname, search, hash, state } = useLocation();
  const estadoDaPagina = (state as EstadoDaPagina | null) ?? {};
  const lateralDoHistorico = estadoDaPagina.equipeLateral ?? null;
  const tituloDaLista = useRef<HTMLHeadingElement>(null);
  const focarLista = useRef(false);
  const abrindoFilaDaLateral = useRef(false);
  const pendencias = usePendenciasDoLider(gestor ? user?._id : undefined, userInfo);
  const { realDatetime: agora, hasTime } = useServerTime();

  const [selecionado, setSelecionado] = useState<LinhaDaEquipe | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [alvoDaTroca, setAlvoDaTroca] = useState<LinhaDaEquipe | null>(null);
  const [avisoAberto, setAvisoAberto] = useState(false);
  const [exportacao, setExportacao] = useState<EstadoDaExportacao>('ocioso');
  const fimDoArquivoGerado = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(fimDoArquivoGerado.current), []);

  // O histórico manda: entrar, sair, voltar e avançar do navegador passam por aqui.
  // A equipe é identificada pela matrícula (`chave`), que todo gestor encontrado tem.
  useEffect(() => {
    if (lateralDoHistorico?.chave === equipe.equipeLateral?.chave) return;
    if (lateralDoHistorico) equipe.verEquipeLateral(lateralDoHistorico);
    else equipe.verMinhaEquipe();
    focarLista.current = true;
    // Só a troca de equipe no histórico importa; `equipe` muda a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lateralDoHistorico?.chave]);

  // MEQ2-07: na equipe lateral, as ações do líder (apreciar, participantes) agem no lugar desse gestor, como na
  // Home: as filas leem a lateralidade. A página só apaga a que ela gravou: ao voltar para a própria equipe e ao sair
  // por outro caminho que não os cartões que abrem as filas desse gestor; assim a fila ECOA e as outras telas abertas
  // pelo menu não a herdam. A escolha feita na Home ou no modal de férias não é desfeita só por passar aqui.
  useEffect(() => {
    if (!lateralDoHistorico?.chave) return undefined;
    gravarLateralidade(lateralDoHistorico.chave);
    abrindoFilaDaLateral.current = false;
    return () => {
      if (!abrindoFilaDaLateral.current) apagarLateralidade();
    };
  }, [lateralDoHistorico?.chave]);

  // Na equipe lateral, a fila abre no lugar desse gestor; na própria equipe, no do funci, sem lateralidade de outra tela.
  function abrirFila() {
    if (lateralDoHistorico) abrindoFilaDaLateral.current = true;
    else apagarLateralidade();
  }

  // MEQ2-04: ao entrar ou sair da equipe lateral, a lista (que mudou) vem para a tela e o leitor de tela ouve o título.
  useEffect(() => {
    if (!focarLista.current) return;
    focarLista.current = false;
    tituloDaLista.current?.focus({ preventScroll: true });
    tituloDaLista.current?.scrollIntoView?.({ behavior: rolagemDaLista(), block: 'start' });
  }, [equipe.equipeLateral]);

  const etapas = useMemo(() => etapasDoCiclo(cycles), [cycles]);
  const janela = useMemo(() => (hasTime ? faseDeReferencia(cycles, agora) : null), [cycles, agora, hasTime]);

  if (!gestor) return null;

  const resumo = equipe.resumo;
  const resumoDaMinhaEquipe = equipe.resumoDaMinhaEquipe;
  const tituloDoCiclo = resumoDaMinhaEquipe?.ciclo.titulo ?? currentCycle?.cycle?.title ?? null;
  const faseAtual = hasTime ? rotuloFaseAtual(etapas, agora) : null;
  const linhas = equipe.funcis.map(funci => linhaDaEquipe(funci, resumo, janela));
  const membroSelecionado = selecionado ? membroDoResumo(resumo, selecionado.chave) : null;

  function solicitacaoConcluida() {
    setModal(null);
    setAlvoDaTroca(null);
    setAvisoAberto(true);
    equipe.recarregar();
  }

  function abrirAlterarGestor(linha: LinhaDaEquipe) {
    setSelecionado(null);
    setAlvoDaTroca(linha);
    setModal('alterar');
  }

  function escolherEquipeLateral(gestorLateral: GestorLateral) {
    setModal(null);
    // De uma equipe lateral para outra, troca a entrada: "voltar" leva sempre à própria equipe.
    const estado: EstadoDaPagina = {
      equipeLateral: gestorLateral,
      sobreAPropriaEquipe: lateralDoHistorico ? estadoDaPagina.sobreAPropriaEquipe : true,
    };
    navigate({ pathname, search, hash }, { state: estado, replace: !!lateralDoHistorico });
  }

  function voltarParaMinhaEquipe() {
    // Sem a própria equipe logo abaixo no histórico, o voltar sairia da página: troca a entrada no lugar.
    if (estadoDaPagina.sobreAPropriaEquipe) navigate(-1);
    else navigate({ pathname, search, hash }, { replace: true, state: null });
  }

  async function exportar() {
    clearTimeout(fimDoArquivoGerado.current);
    setExportacao('gerando');
    try {
      const todos = await equipe.lerEquipeInteira();
      const ciclo = resumo?.ciclo.titulo ?? tituloDoCiclo ?? 'ciclo';
      exportarEquipe(
        todos.map(funci => linhaDaEquipe(funci, resumo, janela)),
        ciclo,
        resumo?.cicloSelos ?? null,
      );
      setExportacao('gerado');
      fimDoArquivoGerado.current = setTimeout(() => setExportacao('ocioso'), DURACAO_DO_ARQUIVO_GERADO_MS);
    } catch {
      setExportacao('erro');
    }
  }

  return (
    <>
      <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        <CabecalhoDaMinhaEquipe
          faseAtual={faseAtual}
          onVoltarParaMinhaEquipe={lateralDoHistorico ? voltarParaMinhaEquipe : undefined}
        />

        <PanoramaDaEquipe
          ciclo={tituloDoCiclo}
          diretoria={userInfo?.diretoria ?? ''}
          totalDeLiderados={equipe.totalDaMinhaEquipe}
          totais={totaisDoPanorama(resumoDaMinhaEquipe?.membros ?? null)}
          cicloSelos={resumoDaMinhaEquipe?.cicloSelos ?? null}
          pendencias={pendencias}
          prazo={hasTime ? textoDoPrazo(etapas, agora) : null}
          mostrarValidar={!rede}
          // A mesma regra de antes, também na equipe lateral: a página de participantes só abre para quem tem alçada.
          mostrarParticipantes={!rede && canApproveParticipants(userInfo)}
          gestorSubstituido={lateralDoHistorico?.nomeGuerra ?? null}
          onAbrirFila={abrirFila}
          mostrarEquipeLateral={!rede}
          onVerEquipeLateral={() => setModal('lateral')}
        />

        <ListaDaEquipe
          linhas={linhas}
          total={equipe.total}
          pagina={equipe.pagina}
          busca={equipe.busca}
          carregando={equipe.carregando}
          carregandoNumeros={equipe.carregandoResumo || !hasTime}
          erroDaLista={equipe.erroDaLista}
          erroDoResumo={equipe.erroDoResumo}
          equipeLateral={equipe.equipeLateral}
          exportacao={exportacao}
          onBuscaChange={equipe.digitarBusca}
          onBuscaSubmit={equipe.enviarBusca}
          onPagina={equipe.irParaPagina}
          onVerMais={setSelecionado}
          onTentarDeNovo={equipe.recarregar}
          onVoltarParaMinhaEquipe={voltarParaMinhaEquipe}
          refDoTitulo={tituloDaLista}
          onExportar={exportar}
          onAdicionar={() => setModal('adicionar')}
        />

        {hasTime && <CalendarioDoCiclo etapas={etapas} agora={agora} />}
      </div>

      <PainelDoLiderado
        linha={selecionado}
        membro={membroSelecionado}
        cycles={cycles}
        podeAlterarGestor={!equipe.equipeLateral}
        onFechar={() => setSelecionado(null)}
        onAlterarGestor={abrirAlterarGestor}
      />

      <ModalAdicionarFuncionario
        isOpen={modal === 'adicionar'}
        onFechar={() => setModal(null)}
        onConcluido={solicitacaoConcluida}
      />
      <ModalAlterarGestor
        isOpen={modal === 'alterar'}
        liderado={alvoDaTroca && { chave: alvoDaTroca.chave, nome: alvoDaTroca.nome }}
        onFechar={() => setModal(null)}
        onConcluido={solicitacaoConcluida}
      />
      <ModalEquipeLateral isOpen={modal === 'lateral'} onFechar={() => setModal(null)} onEscolher={escolherEquipeLateral} />

      <AvisoDeSolicitacao aberto={avisoAberto} onFechar={() => setAvisoAberto(false)} />
    </>
  );
}
